import { registerMetaBuilder } from '../modules/meta-registry.js';

export function initMetaBuilders() {
  // HOMEPAGE
  registerMetaBuilder('home-public-view', () => ({
    docTitle: 'The q1 Network - Hosting the Future of Work - Entrepreneurship, Impact, Emerging Tech',
    title: 'The q1 Network',
    description: 'A global collective, here to help you build the future of work and impact.',
  }));

  // CLAIM-VIEW
  registerMetaBuilder('claim-view', () => ({
    docTitle: 'Claim Existing Membership - q1 Network',
    title: 'Claim Existing Membership',
    description: 'Already a Quorum1 member? Claim and activate your membership in the q1 Network by verifying your email and setting up your profile.',
  }));

  // JOIN-VIEW
  registerMetaBuilder('join-view', () => ({
    docTitle: 'Join the q1 Network - Hosting the Future of Work - Entrepreneurship, Impact, Emerging Tech',
    title: 'Join the q1 Network',
    description: 'Become part of a member-owned community focused on purpose, collaboration, and innovation.',
  }));

  // REGISTER-VIEW
  registerMetaBuilder('register-view', () => ({
    docTitle: 'Register - Join the q1 Network',
    title: 'New Member Registration',
    description: 'Join the q1 Network and become a member of Quorum1 — a collective pioneering the future of work, collaboration, and impact.',
  }));

  // HOME-CONFIRM-VIEW
  registerMetaBuilder('home-confirm-view', () => ({
    docTitle: 'Confirm Your Membership - q1 Network',
    title: 'Confirm Your Membership',
    description: 'Confirm your q1 Network membership by verifying your email and contact details.',
  }));

  // VERIFY-EMAIL-VIEW
  registerMetaBuilder('verify-email-view', () => ({
    docTitle: 'Verify Email - q1 Network',
    title: 'Email Verification',
    description: 'Enter your verification code to confirm your email and complete your registration with the q1 Network.',
  }));

  // VERIFY-PHONE-VIEW
  registerMetaBuilder('verify-phone-view', () => ({
    docTitle: 'Verify Phone - q1 Network',
    title: 'Phone Verification',
    description: 'Enter your verification code to confirm your phone number and activate your membership with the q1 Network.',
  }));

  // HOME-ONBOARD-VIEW
  registerMetaBuilder('home-onboard-view', () => ({
    docTitle: 'Member Onboarding - q1 Network',
    title: 'Member Onboarding',
    description: 'Complete your onboarding steps to activate your q1 Network membership. Required tutorials and profile setup ensure a strong start.',
  }));

  // SIGN-IN-VIEW
  registerMetaBuilder('sign-in-view', () => ({
    docTitle: 'Sign In - q1 Network',
    title: 'Sign In',
    description: 'Access the q1 Network to connect, collaborate, and contribute to impactful work.',
  }));

  // RESET-PASSWORD-VIEW
  registerMetaBuilder('reset-password-view', () => ({
    docTitle: 'Reset Password - q1 Network',
    title: 'Reset Password',
    description: 'Reset your password to access your q1 Network account. Enter your email to request a reset link or create a new password using your verified link.',
  }));

  // HOMEPAGE-SIGNED-IN-VIEW
  registerMetaBuilder('home-signed-in-view', () => ({
    docTitle: 'Home - q1 Network',
    title: 'The q1 Network',
    description: 'Your personal hub for guilds, projects, and actions in the q1 network.',
  }));

  // MEMBER-INDEX-VIEW
  registerMetaBuilder('member-index-view', () => ({
    docTitle: 'Members - q1 Network',
    title: 'Members',
    description: 'Browse the directory of q1 Network members',
  }));

  // MEMBER-PROFILE-VIEW
  registerMetaBuilder('member-profile-view', ({ member } = {}) => ({
    docTitle: `${member.nameExternal || 'Member Profile'} - q1 Network`,
    title: member.nameExternal || 'Member Profile',
    type: 'profile',
    description: member.headline,
    image: member.profile?.standard?.url,
  }));

  // CONTRIBUTOR-UPGRADE-VIEW
  registerMetaBuilder('contributor-upgrade-view', () => ({
    docTitle: 'Contributor Membership - q1 Network',
    title: 'Upgrade to Contributor',
    description: 'Upgrade your membership to Contributor level in the q1 Network. Sign the agreement, join Slack, and begin contributing.',
  }));

  // TUTORIAL-INDEX-VIEW
  registerMetaBuilder('tutorial-index-view', () => ({
    docTitle: 'Member Tutorials - q1 Network',
    title: 'Member Tutorials',
    description: 'Browse and complete onboarding tutorials to get the most out of your q1 Network membership.',
  }));

  // AGREEMENT-VIEW
  registerMetaBuilder('agreement-view', ({ agreement } = {}) => ({
    docTitle: `${agreement?.title || 'Agreement'} - q1 Network`,
    title: agreement?.title || 'Member Agreement',
    type: 'document',
    description: 'Review and respond to a member agreement in the q1 Network. All responses are cryptographically signed for integrity and security.',
  }));

  // VERSION-VIEW
  registerMetaBuilder('version-view', ({ version, parent } = {}) => ({
    docTitle: `${version?.approvalFlow_label || 'Version'} - ${parent?.name || 'q1 Network'}`,
    title: version?.approvalFlow_label || 'Version',
    type: 'document',
    description: 'Review and respond to membership or project approvals, requests, and other versioned decisions in the q1 Network.',
  }));

  // GUILD-INDEX-VIEW
  registerMetaBuilder('guild-index-view', () => ({
    docTitle: 'Guilds - q1 Network',
    title: 'Guilds',
    description: 'Browse all public and member-visible guilds in the q1 Network.',
  }));

  // GUILD-CREATE-VIEW
  registerMetaBuilder('guild-create-view', () => ({
    docTitle: 'Create a Guild - q1 Network',
    title: 'Create a Guild',
    description: 'Start a new guild on the q1 Network to gather members around a shared purpose or interest.',
  }));

  // GUILD-JOIN-VIEW
  registerMetaBuilder('guild-join-view', ({ guild } = {}) => ({
    docTitle: `Join Guild: ${guild?.name || 'Guild'} - q1 Network`,
    title: `Join ${guild?.name || 'a Guild'}`,
    description: guild?.headline || 'Become a member of this guild in the q1 Network.',
    image: guild.avatar?.standard?.url,
  }));

  // GUILD-VIEW
  registerMetaBuilder('guild-view', ({ guild } = {}) => ({
    docTitle: `${guild.name} Guild - q1 Network`,
    title: `${guild.name} Guild`,
    description: guild.headline?.substring?.(0, 300),
    image: guild.avatar?.standard?.url,
  }));

  // GUILD-PROFILE-VIEW
  registerMetaBuilder('guild-profile-view', ({ guild, guildMember } = {}) => ({
    docTitle: `${guildMember?.nameExternal || guildMember?.memberName || 'Guild Member'} - ${guild?.name} - q1 Network`,
    title: guildMember?.headline || `${guildMember?.nameExternal || 'Guild Member'} at ${guild?.name}`,
    type: 'profile',
    description: guildMember?.profile?.substring?.(0, 300) || `${guildMember?.nameExternal || 'This member'} is a ${guildMember?.role_label || 'member'} of ${guild?.name}.`,
    image: guildMember?.profileImageUrl || guild?.avatar?.standard?.url || '/assets/images/q1-banner.png',
  }));

  // PROJECT-INDEX-VIEW
  registerMetaBuilder('project-index-view', () => ({
    docTitle: 'Projects - q1 Network',
    title: 'Projects',
    description: 'Browse all public and member-visible projects in the q1 Network.',
  }));

  // PROJECT-CREATE-VIEW
  registerMetaBuilder('project-create-view', () => ({
    docTitle: 'Create a Project - q1 Network',
    title: 'Create a Project',
    description: 'Launch a new project on the q1 Network to collaborate, innovate, and make an impact.',
  }));

  // PROJECT-JOIN-VIEW
  registerMetaBuilder('project-join-view', ({ project } = {}) => ({
    docTitle: `Join Project: ${project?.name || 'Project'} - q1 Network`,
    title: `Join ${project?.name || 'a Project'}`,
    description: project?.headline || 'Become a member of this project in the q1 Network.',
    image: project.avatar?.standard?.url,
  }));

  // PROJECT-VIEW
  registerMetaBuilder('project-view', ({ project } = {}) => ({
    docTitle: `${project.name} Project - q1 Network`,
    title: `${project.name} Project`,
    description: project.description?.substring?.(0, 300),
    image: project.avatar?.standard?.url,
  }));

  // PROJECT-PROFILE-VIEW
  registerMetaBuilder('project-profile-view', ({ project, projectMember } = {}) => ({
    docTitle: `${projectMember?.nameExternal || projectMember?.memberName || 'Project Member'} - ${project?.name} - q1 Network`,
    title: projectMember?.headline || `${projectMember?.nameExternal || 'Project Member'} at ${project?.name}`,
    type: 'profile',
    description: projectMember?.profile?.substring?.(0, 300) || `${projectMember?.nameExternal || 'This member'} is a ${projectMember?.role_label || 'member'} of ${project?.name}.`,
    image: projectMember?.profileImageUrl || project?.avatar?.standard?.url || '/assets/images/q1-banner.png',
  }));

  // LOG-ITEM-CREATE-VIEW
  registerMetaBuilder('log-item-create-view', () => ({
    docTitle: 'Post Update - q1 Network',
    title: 'Post Log Update',
    description: `Create and publish a new log update to share progress with your community on the q1 Network.`,
  }));

  // LOG ITEM
  registerMetaBuilder('log-item-view', ({ logItem } = {}) => ({
    docTitle: `${logItem.headline || 'Log Item'} - q1 Network`,
    title: logItem.headline || 'Log Item',
    type: 'article',
    description: logItem.body?.substring?.(0, 300) || 'A log entry in the q1 network.',
  }));
}
